import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Json } from "@/integrations/supabase/types";
import { queryKeys } from "@/lib/queryKeys";
import { sendCleaningCompleteEmail } from "@/lib/notificationEmails";
import { notifyOpsHub } from "@/lib/notifyOpsHub";
import { runWithStamp } from "@/lib/stampOutbox";
import { stampColumns, type JobStamp } from "@/lib/locationStamps";
import { scheduleNextPreventativeMaintenanceOnComplete } from "@/lib/scheduleNextPreventativeMaintenance";

export const useCleanerRecord = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["cleaner", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("cleaners")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("[useCleanerRecord] Error fetching cleaner:", error);
        return null;
      }
      return data;
    },
    enabled: !!user,
  });
};

export const useCleanerAssignment = (propertyId: string | undefined) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["cleaner-assignment", propertyId, user?.id],
    queryFn: async () => {
      if (!propertyId || !user) {
        return null;
      }

      // Get cleaner_id from cleaners table
      const { data: cleaner, error: cleanerError } = await supabase
        .from("cleaners")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (cleanerError) {
        throw cleanerError;
      }

      if (!cleaner) {
        return null;
      }

      // Check if row exists in cleaner_assignments
      const { data: assignment, error: assignmentError } = await supabase
        .from("cleaner_assignments")
        .select("id, property_id")
        .eq("cleaner_id", cleaner.id)
        .eq("property_id", propertyId)
        .maybeSingle();

      // Handle 400 as empty state (no assignment = null)
      if (assignmentError && assignmentError.code === '400') {
        return null;
      }
      if (assignmentError) {
        throw assignmentError;
      }

      // DEV-ONLY: Hard invariant guard - detect any NULL property_id in assignment
      if (process.env.NODE_ENV === 'development' && assignment) {
        if (assignment.property_id === null || assignment.property_id === undefined) {
          const error = "INVALID STATE: cleaner_assignment.property_id is NULL in assignment query";
          console.error(error, { assignment, propertyId, cleanerId: cleaner.id });
          throw new Error(error);
        }
      }

      return assignment || null;
    },
    enabled: !!propertyId && !!user,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const usePropertyForCleaner = (propertyId: string | undefined) => {
  const { data: cleaner } = useCleanerRecord();

  return useQuery({
    queryKey: ["property-cleaner", propertyId, cleaner?.id],
    queryFn: async () => {
      // CRITICAL: Validate propertyId before query
      if (!propertyId || propertyId === null || propertyId === undefined || propertyId.trim() === '') {
        throw new Error("CRITICAL: usePropertyForCleaner called with null/undefined propertyId");
      }

      if (!cleaner) return null;

      // Two-step fetch to avoid 400 from PostgREST embed (relation may not be exposed or RLS blocks join).
      // Step 1: Check assignment via cleaner_assignments (no embed).
      const { data: assignmentRow, error: assignmentError } = await supabase
        .from("cleaner_assignments")
        .select("property_id")
        .eq("cleaner_id", cleaner.id)
        .eq("property_id", propertyId)
        .maybeSingle();

      if (assignmentError) {
        if (assignmentError.code === "400" || assignmentError.code === "PGRST116") {
          // Fallback: cleaner may have task but no assignment row (e.g. invited cleaner)
          const { data: propRow, error: propError } = await supabase
            .from("properties")
            .select("id, name, address, unit, city, state_province, postal_code, country, formatted_address, latitude, longitude, property_photo_url, property_code, status, deleted_at, owner_only_assignment, default_checklist_id, notes, entry_method, entry_code, entry_location, unit_location, parking_instructions, supply_closet_info, access_notes, key_location_reference_urls")
            .eq("id", propertyId)
            .maybeSingle();
          if (!propError && propRow) return propRow;
          throw new Error(`Cleaner not assigned to property ${propertyId}. Assignment may be missing.`);
        }
        throw assignmentError;
      }

      // Step 2: Fetch property by id. RLS "Cleaners can view property for their task" or assignment allows this.
      const { data: property, error: propError } = await supabase
        .from("properties")
        .select("id, name, address, unit, city, state_province, postal_code, country, formatted_address, latitude, longitude, property_photo_url, property_code, status, deleted_at, owner_only_assignment, default_checklist_id, notes, entry_method, entry_code, entry_location, unit_location, parking_instructions, supply_closet_info, access_notes, key_location_reference_urls")
        .eq("id", propertyId)
        .maybeSingle();

      if (propError) throw propError;
      if (assignmentRow && !property) {
        throw new Error(`Assignment exists for property ${propertyId} but property data could not be loaded.`);
      }
      return property || null;
    },
    enabled: !!propertyId && !!cleaner,
    retry: false,
  });
};

export const useChecklistForCleaner = (checklistId: string | null | undefined) => {
  return useQuery({
    queryKey: ["checklist-cleaner", checklistId],
    queryFn: async () => {
      if (!checklistId) return null;
      const { data, error } = await supabase
        .from("checklists")
        .select("*")
        .eq("id", checklistId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!checklistId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 0,
  });
};

export const useInventoryForCleaner = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["inventory-cleaner", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("inventory_items")
        .select("*")
        .eq("property_id", propertyId)
        .order("item_name", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!propertyId,
  });
};

export const useSubmitCleaning = () => {
  const { data: cleaner } = useCleanerRecord();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      checklistId,
      notes,
      issues,
      photos,
      tasksStatus,
      completionPercentage,
      submissionId: preGeneratedId,
      cleaningTaskId,
      access_verification_snapshot,
      entry_condition_snapshot,
    }: {
      propertyId: string;
      checklistId: string | null;
      notes: string;
      issues: { room_name: string; description: string; photo_url: string | null; severity?: string }[];
      photos: { room_name: string; photo_url: string; task_text: string | null }[];
      tasksStatus: { room_name: string; task_text: string; completed: boolean }[];
      completionPercentage: number;
      submissionId?: string;
      cleaningTaskId?: string | null;
      access_verification_snapshot?: Json | null;
      entry_condition_snapshot?: Json | null;
    }) => {
      if (!cleaner) throw new Error("Not a registered cleaner");
      const validSeverity = (s: string | undefined) =>
        s && ["low", "medium", "high", "urgent"].includes(s) ? s : "medium";

      let currentTask: Record<string, unknown> | null = null;
      let currentTaskId: string | null = null;

      if (cleaningTaskId) {
        const { data: byId, error: taskErr } = await supabase
          .from("cleaning_tasks")
          .select("*")
          .eq("id", cleaningTaskId)
          .eq("property_id", propertyId)
          .maybeSingle();
        if (taskErr) throw taskErr;
        if (!byId) throw new Error("Cleaning task not found for this property");
        currentTask = byId as Record<string, unknown>;
        currentTaskId = byId.id as string;
      } else {
        const today = new Date().toISOString().split("T")[0];
        const { data: activeTasks } = await supabase
          .from("cleaning_tasks")
          .select("*")
          .eq("property_id", propertyId)
          .in("status", ["scheduled", "in_progress"])
          .lte("scheduled_date", today)
          .order("scheduled_date", { ascending: true })
          .limit(1);
        currentTaskId = activeTasks?.[0]?.id || null;
        currentTask = (activeTasks?.[0] as Record<string, unknown>) || null;
      }

      const entryState = currentTask?.entry_walkthrough_state as Record<string, unknown> | undefined;
      const resolvedAccess =
        access_verification_snapshot ??
        (entryState?.access != null ? (entryState.access as Json) : null);
      const resolvedCondition: Json | null =
        entry_condition_snapshot ??
        (() => {
          if (!entryState) return null;
          const cond = entryState.condition_photos_by_room;
          const proof = entryState.proof_media_by_standard_id;
          const functional = entryState.functional_check_pass_standard_ids;
          if (cond == null && proof == null && functional == null) return null;
          const merged: Record<string, unknown> = {};
          if (cond != null) merged.condition_photos_by_room = cond;
          if (proof != null) merged.proof_media_by_standard_id = proof;
          if (functional != null) merged.functional_check_pass_standard_ids = functional;
          return merged as Json;
        })();

      const { data, error } = await supabase
        .from("cleaning_submissions")
        .insert({
          id: preGeneratedId, // Use pre-generated ID if provided (for task_submission_photos)
          property_id: propertyId,
          cleaner_id: cleaner.id,
          checklist_id: checklistId,
          cleaning_task_id: currentTaskId,
          notes,
          issues: issues as unknown as Json,
          photos: photos as unknown as Json,
          tasks_status: tasksStatus as unknown as Json,
          completion_percentage: completionPercentage,
          access_verification_snapshot: resolvedAccess,
          entry_condition_snapshot: resolvedCondition,
        })
        .select()
        .single();

      if (error) throw error;

      // Insert issues into maintenance_issues table for proper tracking
      // Check for duplicates before inserting
      if (issues.length > 0) {
        for (const issue of issues) {
          // Check if this exact issue already exists for this submission
          const { data: existingIssue } = await supabase
            .from("maintenance_issues")
            .select("id")
            .eq("cleaning_submission_id", data.id)
            .eq("room_name", issue.room_name)
            .eq("description", issue.description)
            .maybeSingle();

          // Only insert if not already exists
          if (!existingIssue) {
            const { data: inserted } = await supabase
              .from("maintenance_issues")
              .insert({
                property_id: propertyId,
                cleaning_submission_id: data.id,
                cleaning_task_id: currentTaskId,
                cleaner_id: cleaner.id,
                room_name: issue.room_name,
                description: issue.description,
                photo_url: issue.photo_url,
                status: "open",
                severity: validSeverity(issue.severity),
              })
              .select("id")
              .single();
            if (inserted?.id && issue.photo_url) {
              const { triggerDamageClaimGeneration } = await import("@/lib/generateDamageClaim");
              triggerDamageClaimGeneration(inserted.id, propertyId);
            }
          }
        }
      }

      // Mark checklist_completed only when ALL assigned checklists for this user have been submitted
      if (currentTask) {
        const { data: assignmentRows } = await supabase
          .from("cleaning_task_checklist_assignments")
          .select("checklist_id")
          .eq("cleaning_task_id", currentTaskId)
          .eq("cleaner_id", cleaner.id);
        const assignedIds = (assignmentRows ?? []).length > 0
          ? (assignmentRows ?? []).map((r) => r.checklist_id)
          : (currentTask.checklist_id ? [currentTask.checklist_id] : []);

        const { data: submissions } = await supabase
          .from("cleaning_submissions")
          .select("checklist_id")
          .eq("cleaning_task_id", currentTaskId)
          .eq("cleaner_id", cleaner.id);
        const submittedIds = new Set((submissions ?? []).map((s) => s.checklist_id).filter(Boolean));
        const allAssignedSubmitted = assignedIds.length > 0 && assignedIds.every((id) => submittedIds.has(id));

        const updateData: Record<string, unknown> = {
          cleaner_id: cleaner.id,
          ...(allAssignedSubmitted && { checklist_completed: true }),
        };

        // If property has no inventory items, checklist submit = task fully complete (no inventory step).
        const { count: inventoryCount } = await supabase
          .from("inventory_items")
          .select("*", { count: "exact", head: true })
          .eq("property_id", propertyId);
        const hasInventory = (inventoryCount ?? 0) > 0;

        // Both complete = all checklists submitted AND (inventory already done OR property has no inventory)
        const bothWillBeComplete =
          allAssignedSubmitted && (currentTask.inventory_completed === true || !hasInventory);

        // Complete task and calculate duration when BOTH are done
        if (bothWillBeComplete && !currentTask.completed_at) {
          const now = new Date();
          updateData.status = "completed";
          updateData.completed_at = now.toISOString();
          if (!hasInventory) {
            (updateData as Record<string, unknown>).inventory_completed = true;
          }
          if (currentTask.started_at && currentTask.duration_minutes === null) {
            const startTime = new Date(currentTask.started_at).getTime();
            updateData.duration_minutes = Math.ceil((now.getTime() - startTime) / 60000);
          }
        }

        const { error: updateError } = await supabase
          .from("cleaning_tasks")
          .update(updateData)
          .eq("id", currentTask.id);

        if (updateError) {
          console.error("[useSubmitCleaning] Failed to update task:", updateError);
          throw updateError;
        }

        if (bothWillBeComplete) {
          await supabase.rpc("snapshot_cleaning_task_fee_from_agreement", { p_task_id: currentTask.id }).then(() => {}).catch(() => {});
        }
      }

      return data;
    },
    onSuccess: (_, variables) => {
      // RACE CONDITION FIX: Do NOT refetch immediately after submission
      // Query invalidation is deferred until AFTER flow state advances in the component
      // This prevents React Query from overwriting UI state mid-transition
      // The component will trigger refetch after state machine advances to next state
      // 
      // Invalidate submissions so owner Reports tab shows the new report when they view it
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
      // Note: No refetchQueries() call here - refetch happens after state transition
    },
  });
};

/** Submit checklist as owner or team (no cleaner; uses submitted_by_user_id). Skips maintenance_issues rows (issues stored in submission JSON). */
export const useSubmitCleaningAsOwnerOrTeam = (taskId: string | undefined, propertyId: string | undefined) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId: pId,
      checklistId: submittedChecklistId,
      notes,
      issues,
      photos,
      tasksStatus,
      completionPercentage,
      submissionId: preGeneratedId,
    }: {
      propertyId: string;
      checklistId?: string | null;
      notes: string;
      issues: { room_name: string; description: string; photo_url: string | null; severity?: string }[];
      photos: { room_name: string; photo_url: string; task_text: string | null }[];
      tasksStatus: { room_name: string; task_text: string; completed: boolean }[];
      completionPercentage: number;
      submissionId?: string;
    }) => {
      const propId = pId || propertyId;
      if (!user?.id || !taskId || !propId) throw new Error("Not authenticated or missing task");

      const { data: currentTask, error: taskErr } = await supabase
        .from("cleaning_tasks")
        .select(
          "id, checklist_id, checklist_completed, inventory_completed, completed_at, started_at, duration_minutes, entry_walkthrough_state"
        )
        .eq("id", taskId)
        .eq("property_id", propId)
        .single();

      if (taskErr || !currentTask) throw new Error("Task not found");

      const checklistIdForInsert = submittedChecklistId ?? currentTask.checklist_id ?? null;

      const entryState = (currentTask as { entry_walkthrough_state?: unknown }).entry_walkthrough_state as
        | Record<string, unknown>
        | undefined;
      const resolvedAccess = entryState?.access != null ? (entryState.access as Json) : null;
      const resolvedCondition: Json | null = (() => {
        if (!entryState) return null;
        const cond = entryState.condition_photos_by_room;
        const proof = entryState.proof_media_by_standard_id;
        const functional = entryState.functional_check_pass_standard_ids;
        if (cond == null && proof == null && functional == null) return null;
        const merged: Record<string, unknown> = {};
        if (cond != null) merged.condition_photos_by_room = cond;
        if (proof != null) merged.proof_media_by_standard_id = proof;
        if (functional != null) merged.functional_check_pass_standard_ids = functional;
        return merged as Json;
      })();

      const { data, error } = await supabase
        .from("cleaning_submissions")
        .insert({
          id: preGeneratedId,
          property_id: propId,
          cleaner_id: null as unknown as string,
          submitted_by_user_id: user.id,
          checklist_id: checklistIdForInsert ?? null,
          cleaning_task_id: taskId,
          notes,
          issues: issues as unknown as Json,
          photos: photos as unknown as Json,
          tasks_status: tasksStatus as unknown as Json,
          completion_percentage: completionPercentage,
          access_verification_snapshot: resolvedAccess,
          entry_condition_snapshot: resolvedCondition,
        })
        .select()
        .single();

      if (error) throw error;

      const { data: assignmentRows } = await supabase
        .from("cleaning_task_checklist_assignments")
        .select("checklist_id")
        .eq("cleaning_task_id", taskId)
        .eq("assignee_user_id", user.id);
      const assignedIds = (assignmentRows ?? []).length > 0
        ? (assignmentRows ?? []).map((r) => r.checklist_id)
        : (currentTask.checklist_id ? [currentTask.checklist_id] : []);
      const { data: submissions } = await supabase
        .from("cleaning_submissions")
        .select("checklist_id")
        .eq("cleaning_task_id", taskId)
        .not("submitted_by_user_id", "is", null);
      const submittedIds = new Set((submissions ?? []).map((s) => s.checklist_id).filter(Boolean));
      const allAssignedSubmitted = assignedIds.length > 0 && assignedIds.every((id) => submittedIds.has(id));

      const updateData: Record<string, unknown> = {};
      if (allAssignedSubmitted) updateData.checklist_completed = true;
      const bothWillBeComplete = allAssignedSubmitted && currentTask.inventory_completed === true;
      if (bothWillBeComplete && !currentTask.completed_at) {
        const now = new Date();
        updateData.status = "completed";
        updateData.completed_at = now.toISOString();
        if (currentTask.started_at && currentTask.duration_minutes === null) {
          updateData.duration_minutes = Math.ceil((now.getTime() - new Date(currentTask.started_at).getTime()) / 60000);
        }
      }

      if (Object.keys(updateData).length > 0) {
        const { error: updateErr } = await supabase.from("cleaning_tasks").update(updateData).eq("id", taskId);
        if (updateErr) throw updateErr;
      }
      if (bothWillBeComplete) {
        await supabase.rpc("snapshot_cleaning_task_fee_from_agreement", { p_task_id: taskId }).then(() => {}).catch(() => {});
      }

      return data;
    },
    onSuccess: (_, variables) => {
      const propId = variables.propertyId || propertyId;
      if (propId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.submissions.byProperty(propId) });
        queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propId] });
        queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propId] });
        queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      }
    },
  });
};

/** Submit inventory when executor is owner/team (cleaner_id null, submitted_by_user_id set). */
export const useSubmitInventoryAsOwnerOrTeam = (cleaningTaskId: string | undefined, propertyId: string | undefined) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId: pId,
      items,
      cleaningTaskId: taskIdParam,
      onlyUpdateItems = false,
      inventorySubmitPhase,
      closeout,
      closeoutOnly = false,
    }: {
      propertyId: string;
      items: { item_id: string; item_name: string; count: number; fullness_value?: number }[];
      cleaningTaskId?: string | null;
      onlyUpdateItems?: boolean;
      inventorySubmitPhase?: "guest" | "final";
      closeout?: {
        keys_returned_confirmed: boolean;
        departure_notes?: string | null;
        keys_photo_url?: string | null;
      };
      /** Counts already saved; only write closeout flags on the task (+ optional submission row). */
      closeoutOnly?: boolean;
    }) => {
      if (!user?.id || !taskIdParam || !pId) throw new Error("Not authenticated or missing task");

      let submission: unknown = null;
      if (!closeoutOnly && items.length > 0) {
        if (!onlyUpdateItems) {
          const { data: inserted, error: submitError } = await supabase
            .from("inventory_submissions")
            .insert({
              property_id: pId,
              cleaner_id: null as unknown as string,
              submitted_by_user_id: user.id,
              items: items as unknown as Json,
            })
            .select()
            .single();
          if (submitError) throw submitError;
          submission = inserted;
        }
        for (const item of items) {
          const updateData: Record<string, unknown> = {};
          if (item.fullness_value !== undefined) {
            updateData.current_value = item.fullness_value;
          } else {
            const count = Math.max(0, item.count);
            updateData.current_count = count;
            updateData.in_stock_qty = count;
          }
          const { data: updatedRows, error: itemUpdErr } = await supabase
            .from("inventory_items")
            .update(updateData)
            .eq("id", item.item_id)
            .eq("property_id", pId)
            .select("id");
          if (itemUpdErr) throw itemUpdErr;
          if (!updatedRows?.length) {
            throw new Error(
              `Could not save inventory for "${item.item_name}" (permission or missing row). Ask the host to confirm your property access.`
            );
          }
        }
      }

      if (onlyUpdateItems) return submission;

      const { data: task, error: taskErr } = await supabase
        .from("cleaning_tasks")
        .select(
          "id, checklist_completed, completed_at, started_at, duration_minutes, inventory_guest_phase_completed, inventory_supply_phase_completed"
        )
        .eq("id", taskIdParam)
        .eq("property_id", pId)
        .maybeSingle();
      if (taskErr || !task) throw new Error("Task not found");

      const phase = inventorySubmitPhase ?? "final";
      const upd: Record<string, unknown> = {};
      if (phase === "guest") {
        upd.inventory_guest_phase_completed = true;
      } else {
        upd.inventory_guest_phase_completed = true;
        upd.inventory_supply_phase_completed = true;
        upd.inventory_completed = true;
        if (closeout) {
          upd.closeout_keys_confirmed = closeout.keys_returned_confirmed;
          upd.closeout_notes = closeout.departure_notes ?? null;
          upd.closeout_keys_photo_url = closeout.keys_photo_url ?? null;
          upd.closeout_state = {
            keys_returned_confirmed: closeout.keys_returned_confirmed,
            departure_notes: closeout.departure_notes ?? null,
            keys_photo_url: closeout.keys_photo_url ?? null,
            saved_at: new Date().toISOString(),
          };
        }
      }

      const { error: updateError } = await supabase.from("cleaning_tasks").update(upd).eq("id", task.id);
      if (updateError) throw updateError;
      return submission;
    },
    onSuccess: (_, variables) => {
      const propId = variables.propertyId || propertyId;
      if (propId) {
        queryClient.invalidateQueries({ queryKey: ["inventory-cleaner", propId] });
        queryClient.invalidateQueries({ queryKey: ["inventory", propId] });
        queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propId] });
        queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propId] });
        queryClient.invalidateQueries({ queryKey: queryKeys.submissions.byProperty(propId) });
        queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      }
    },
  });
};

export const useSubmitInventory = () => {
  const { data: cleaner } = useCleanerRecord();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      items,
      cleaningTaskId: cleaningTaskIdParam,
      onlyUpdateItems = false,
      inventorySubmitPhase,
      closeout,
      closeoutOnly = false,
    }: {
      propertyId: string;
      items: { item_id: string; item_name: string; count: number; fullness_value?: number }[];
      cleaningTaskId?: string | null;
      /** When true, only update inventory item rows; do not touch cleaning_tasks (avoids auto-ending the clean). */
      onlyUpdateItems?: boolean;
      /** guest = only mark guest-amenities phase; final = complete turnover inventory flags (default). */
      inventorySubmitPhase?: "guest" | "final";
      closeout?: {
        keys_returned_confirmed: boolean;
        departure_notes?: string | null;
        keys_photo_url?: string | null;
      };
      /** Counts already saved; only write closeout flags on the task (+ optional submission row). */
      closeoutOnly?: boolean;
    }) => {
      if (!cleaner) throw new Error("Not a registered cleaner");

      let submission: unknown = null;

      if (!closeoutOnly && items.length > 0) {
        if (!onlyUpdateItems) {
          const { data: inserted, error: submitError } = await supabase
            .from("inventory_submissions")
            .insert({
              property_id: propertyId,
              cleaner_id: cleaner.id,
              items: items as unknown as Json,
            })
            .select()
            .single();
          if (submitError) throw submitError;
          submission = inserted;
        }

        for (const item of items) {
          const updateData: Record<string, unknown> = {};
          if (item.fullness_value !== undefined) {
            updateData.current_value = item.fullness_value;
          } else {
            const count = Math.max(0, item.count);
            updateData.current_count = count;
            updateData.in_stock_qty = count;
          }
          const { data: updatedRows, error: itemUpdErr } = await supabase
            .from("inventory_items")
            .update(updateData)
            .eq("id", item.item_id)
            .eq("property_id", propertyId)
            .select("id");
          if (itemUpdErr) throw itemUpdErr;
          if (!updatedRows?.length) {
            throw new Error(
              `Could not save inventory for "${item.item_name}" (permission or missing row). Ask the host to confirm your property access.`
            );
          }
        }
      }

      // When only updating items (e.g. single fullness click), do not touch cleaning_tasks
      if (onlyUpdateItems) return submission;

      // Mark phased inventory on cleaning_tasks; never set status=completed here — owner uses "Complete Cleaning"
      type TaskRow = {
        id: string;
        checklist_completed: boolean;
        inventory_completed: boolean | null;
        inventory_guest_phase_completed?: boolean | null;
        inventory_supply_phase_completed?: boolean | null;
        completed_at: string | null;
        started_at: string | null;
        duration_minutes: number | null;
      };
      let task: TaskRow | null = null;

      if (cleaningTaskIdParam) {
        const { data: byId, error: byIdError } = await supabase
          .from("cleaning_tasks")
          .select(
            "id, checklist_completed, inventory_completed, inventory_guest_phase_completed, inventory_supply_phase_completed, completed_at, started_at, duration_minutes"
          )
          .eq("id", cleaningTaskIdParam)
          .eq("property_id", propertyId)
          .maybeSingle();
        if (!byIdError && byId) task = byId as TaskRow;
      }

      if (!task) {
        let { data: activeTasks } = await supabase
          .from("cleaning_tasks")
          .select("*")
          .eq("property_id", propertyId)
          .eq("cleaner_id", cleaner.id)
          .eq("status", "in_progress")
          .order("scheduled_date", { ascending: true })
          .limit(1);
        if (!activeTasks || activeTasks.length === 0) {
          const today = new Date().toISOString().split("T")[0];
          const { data: scheduledTasks } = await supabase
            .from("cleaning_tasks")
            .select("*")
            .eq("property_id", propertyId)
            .eq("cleaner_id", cleaner.id)
            .eq("status", "scheduled")
            .lte("scheduled_date", today)
            .order("scheduled_date", { ascending: true })
            .limit(1);
          activeTasks = scheduledTasks;
        }
        if (activeTasks && activeTasks.length > 0) task = activeTasks[0] as TaskRow;
      }

      if (!task) {
        throw new Error("No cleaning task found to update. Finish the checklist first, or try again.");
      }

      const phase = inventorySubmitPhase ?? "final";
      // Do not overwrite cleaner_id — team/owner tasks force cleaner_id NULL via trigger;
      // rewriting it can fail assignment validation mid-inventory.
      const taskUpdate: Record<string, unknown> = {};

      if (phase === "guest") {
        taskUpdate.inventory_guest_phase_completed = true;
      } else {
        taskUpdate.inventory_guest_phase_completed = true;
        taskUpdate.inventory_supply_phase_completed = true;
        taskUpdate.inventory_completed = true;
        if (closeout) {
          taskUpdate.closeout_keys_confirmed = closeout.keys_returned_confirmed;
          taskUpdate.closeout_notes = closeout.departure_notes ?? null;
          taskUpdate.closeout_keys_photo_url = closeout.keys_photo_url ?? null;
          taskUpdate.closeout_state = {
            keys_returned_confirmed: closeout.keys_returned_confirmed,
            departure_notes: closeout.departure_notes ?? null,
            keys_photo_url: closeout.keys_photo_url ?? null,
            saved_at: new Date().toISOString(),
          };
        }
      }

      const { error: upErr } = await supabase.from("cleaning_tasks").update(taskUpdate).eq("id", task.id);
      if (upErr) throw upErr;

      return submission;
    },
    onSuccess: (_, variables) => {
      // RACE CONDITION FIX: Do NOT refetch immediately after inventory submission
      // Query invalidation is deferred until AFTER flow state advances to "complete"
      // Invalidate so owner Reports and Performance tabs show the completed task
      queryClient.invalidateQueries({ queryKey: ["inventory-cleaner", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["inventory", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property-status-badges"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cleaners.performance(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
    },
  });
};

// Hook to mark cleaning task as completed (used when reaching complete state)
export const useMarkTaskComplete = () => {
  const { data: cleaner } = useCleanerRecord();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      propertyId,
      replayStamp,
    }: {
      taskId: string;
      propertyId: string;
      /** Set only when the offline outbox replays a queued finish: the stamp as it was taken. */
      replayStamp?: JobStamp;
    }) => {
      if (!cleaner) throw new Error("Not a registered cleaner");
      return runWithStamp<{ propertyId: string; taskId: string }>({
        kind: "finish_cleaner",
        taskId,
        propertyId,
        replay: replayStamp ? { stamp: replayStamp } : undefined,
        queuedResult: () => ({ propertyId, taskId }),
        run: async (stamp, stampSettings) => {

      // Get current task to calculate duration
      const { data: task, error: fetchError } = await supabase
        .from("cleaning_tasks")
        .select("*")
        .eq("id", taskId)
        .single();

      if (fetchError) throw fetchError;
      if (!task) throw new Error("Task not found");

      const now = new Date(stamp.at);
      const updateData: Record<string, unknown> = {
        status: "completed",
        checklist_completed: true,
        inventory_completed: true,
        cleaner_id: cleaner.id,
      };

      // Only set completed_at if not already set (don't overwrite)
      if (!task.completed_at) {
        updateData.completed_at = now.toISOString();
        Object.assign(updateData, stampColumns("finish", stamp, stampSettings));
      }

      // Calculate duration from started_at only if not already stored
      if (task.started_at && task.duration_minutes === null) {
        const startTime = new Date(task.started_at).getTime();
        const durationMs = now.getTime() - startTime;
        updateData.duration_minutes = Math.ceil(durationMs / 60000);
      }

      const { error } = await supabase
        .from("cleaning_tasks")
        .update(updateData)
        .eq("id", taskId);

      if (error) throw error;

      await supabase.rpc("snapshot_cleaning_task_fee_from_agreement", { p_task_id: taskId }).then(() => {}).catch(() => {});

      const scheduledDate = (task as { scheduled_date?: string }).scheduled_date;
      if (scheduledDate) {
        const { data: billing } = await supabase.from("property_billing_settings").select("invoice_frequency").eq("property_id", propertyId).maybeSingle();
        if ((billing as { invoice_frequency?: string } | null)?.invoice_frequency === "per_cleaning") {
          await supabase.rpc("create_invoice_from_period", {
            p_property_id: propertyId,
            p_period_start: scheduledDate,
            p_period_end: scheduledDate,
          }).then(() => {}).catch(() => {});
        }
      }

      if (!(task as { is_test?: boolean }).is_test) sendCleaningCompleteEmail({
        propertyId,
        taskId,
        cleanerName: cleaner?.full_name ?? null,
        scheduledDate: scheduledDate ?? null,
        durationMinutes: typeof updateData.duration_minutes === "number" ? updateData.duration_minutes : null,
      }).catch(() => {});

      if (!(task as { is_test?: boolean }).is_test) {
        void notifyOpsHub({
          event: "cleaning_complete",
          propertyId,
          cleaningTaskId: taskId,
        });
      }

      await scheduleNextPreventativeMaintenanceOnComplete(taskId);

      return { propertyId, taskId };
        },
      });
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.byProperty(data.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["invoices-by-property", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["my-preventative-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      await queryClient.invalidateQueries({ queryKey: ["cleaner-past-cleanings"] });
      await queryClient.refetchQueries({ queryKey: ["cleaner-past-cleanings"] });
    },
  });
};

/** Mark task complete when executor is owner or team (no cleaner_id). */
export const useMarkTaskCompleteAsOwnerOrTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      propertyId,
      replayStamp,
    }: {
      taskId: string;
      propertyId: string;
      /** Set only when the offline outbox replays a queued finish: the stamp as it was taken. */
      replayStamp?: JobStamp;
    }) =>
      runWithStamp<{ propertyId: string; taskId: string }>({
        kind: "finish_team",
        taskId,
        propertyId,
        replay: replayStamp ? { stamp: replayStamp } : undefined,
        queuedResult: () => ({ propertyId, taskId }),
        run: async (stamp, stampSettings) => {
      const { data: task, error: fetchError } = await supabase
        .from("cleaning_tasks")
        .select("*")
        .eq("id", taskId)
        .single();

      if (fetchError) throw fetchError;
      if (!task) throw new Error("Task not found");

      const now = new Date(stamp.at);
      const updateData: Record<string, unknown> = {
        status: "completed",
        checklist_completed: true,
        inventory_completed: true,
      };
      if (!task.completed_at) {
        updateData.completed_at = now.toISOString();
        Object.assign(updateData, stampColumns("finish", stamp, stampSettings));
      }
      if (task.started_at && task.duration_minutes === null) {
        const startTime = new Date(task.started_at).getTime();
        updateData.duration_minutes = Math.ceil((now.getTime() - startTime) / 60000);
      }

      const { error } = await supabase.from("cleaning_tasks").update(updateData).eq("id", taskId);
      if (error) throw error;

      await supabase.rpc("snapshot_cleaning_task_fee_from_agreement", { p_task_id: taskId }).then(() => {}).catch(() => {});

      const scheduledDate = (task as { scheduled_date?: string }).scheduled_date;
      if (scheduledDate) {
        const { data: billing } = await supabase.from("property_billing_settings").select("invoice_frequency").eq("property_id", propertyId).maybeSingle();
        if ((billing as { invoice_frequency?: string } | null)?.invoice_frequency === "per_cleaning") {
          await supabase.rpc("create_invoice_from_period", {
            p_property_id: propertyId,
            p_period_start: scheduledDate,
            p_period_end: scheduledDate,
          }).then(() => {}).catch(() => {});
        }
      }

      if (!(task as { is_test?: boolean }).is_test) {
        void notifyOpsHub({
          event: "cleaning_complete",
          propertyId,
          cleaningTaskId: taskId,
        });
      }

      await scheduleNextPreventativeMaintenanceOnComplete(taskId);

      return { propertyId, taskId };
        },
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.byProperty(data.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["invoices-by-property", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["my-preventative-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["cleaner-past-cleanings"] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
    },
  });
};
